// src/lib/stores/vitals.svelte.ts
// Global organism vitals — drives the living/breathing UI aesthetic.
// The pulse rate quickens proportionally to active agent count.
// Heat levels are computed from card.updated_at timestamps.
import { agentStore } from './agents.svelte.ts';

// ── Heat calculation ──────────────────────────────────────────────────────────
// Returns a 0–1 heat value: 1 = updated just now, 0 = updated 30+ minutes ago.
export function cardHeat(updatedAt: string): number {
  const age = Date.now() - new Date(updatedAt).getTime();
  const maxAge = 30 * 60 * 1000; // 30 minutes
  return Math.max(0, 1 - age / maxAge);
}

// Interpolates between cool purple (resting) and warm gold (just-touched).
// Heat 0 = dim amethyst border; Heat 1 = glowing honey/gold bloom.
// Implemented in OKLCH so hue shifts stay perceptually smooth.
export function heatBorderColor(heat: number): string {
  if (heat <= 0) return 'oklch(0.62 0.22 310 / 0.14)';
  // Hue: 310 (purple) → 85 (gold). Go the short way via 25 is ugly, so lerp
  // linearly from 310 down through 200-ish to 85 by taking the "down" path:
  // 310 - heat*(310-85) = 310 - heat*225.
  const hue        = 310 - heat * 225;
  const lightness  = 0.62 + heat * 0.22;   // 0.62 → 0.84
  const chroma     = 0.22 + heat * 0.02;   // 0.22 → 0.24
  const alpha      = 0.14 + heat * 0.42;   // 0.14 → 0.56
  return `oklch(${lightness.toFixed(2)} ${chroma.toFixed(2)} ${hue.toFixed(0)} / ${alpha.toFixed(2)})`;
}

export function heatGlow(heat: number): string {
  if (heat <= 0) return 'none';
  const hue        = 310 - heat * 225;
  const lightness  = 0.65 + heat * 0.20;
  const chroma     = 0.24;
  const spread     = Math.round(6 + heat * 22);   // 6 → 28 px
  const alpha      = (heat * 0.45).toFixed(2);
  return `0 0 ${spread}px oklch(${lightness.toFixed(2)} ${chroma} ${hue.toFixed(0)} / ${alpha})`;
}

// ── Pulse rate (organism breathing) ──────────────────────────────────────────
// Idle: 10s cycle. Each active agent shaves ~1s off, floor at 2s.
function computePulseRate(): number {
  const count = agentStore.running.length;
  return Math.max(2, 10 - count * 1.5);
}

// ── CSS var application ───────────────────────────────────────────────────────
// Call applyVitals() once on app mount; it sets CSS custom properties on :root
// and keeps them in sync with agentStore changes via $effect.
let _applied = false;

export function applyVitals() {
  if (_applied) return;
  _applied = true;

  // We use a MutationObserver-free approach: just set on document.documentElement
  // and call update whenever agentStore changes (managed by caller via $effect).
  updateVitalsCss();
}

export function updateVitalsCss() {
  const rate = computePulseRate();
  const count = agentStore.running.length;
  const intensity = Math.min(1, 0.3 + count * 0.15);
  document.documentElement.style.setProperty('--pulse-rate', `${rate}s`);
  document.documentElement.style.setProperty('--pulse-intensity', intensity.toFixed(2));
  document.documentElement.style.setProperty('--active-agents', String(count));
}
